import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
} from '@angular/forms';
import { CustomSelectOption } from 'src/app/core/interfaces/custom-select-option';
import { LoadingBay } from 'src/app/core/models/loading-bay';
import { LoadingBayService } from 'src/app/core/services/general/loading-bay.service';

@Component({
  selector: 'app-loading-bay-input',
  templateUrl: './loading-bay-input.component.html',
  styleUrls: ['./loading-bay-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: LoadingBayInputComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: LoadingBayInputComponent,
    },
  ],
})
export class LoadingBayInputComponent implements OnChanges {
  @Input()
  title = "Select Loading Bay...";

  @Input()
  isDisabled = false;

  @Input()
  filterWarehouseUuid = null;

  public value?: string;
  public localValue?: CustomSelectOption;

  public loadingBaysList: Array<CustomSelectOption> = [];
  public loadingBays: Array<LoadingBay> = [];
  public loading = false;

  constructor(
    private loadingBayService: LoadingBayService,
  ) {
    // Don't load loading bays in constructor anymore
    // They will be loaded when filterWarehouseUuid is provided
  }

  ngOnInit(): void {
    // Load loading bays if filterWarehouseUuid is already available
    if (this.filterWarehouseUuid) {
      this.loadLoadingBaysForWarehouse(this.filterWarehouseUuid);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // React to changes in filterWarehouseUuid
    if (changes['filterWarehouseUuid']) {
      const currentValue = changes['filterWarehouseUuid'].currentValue;
      const previousValue = changes['filterWarehouseUuid'].previousValue;

      // If warehouse changed, clear current selection and load new loading bays
      if (currentValue !== previousValue) {
        this.clearSelection();

        if (currentValue) {
          this.loadLoadingBaysForWarehouse(currentValue);
        } else {
          // Clear loading bays if no warehouse selected
          this.clearLoadingBays();
        }
      }
    }
  }

  private loadLoadingBaysForWarehouse(warehouseUuid: string): void {
    if (!warehouseUuid) {
      this.clearLoadingBays();
      return;
    }

    this.loading = true;
    this.loadingBayService
      .allByWarehouse(warehouseUuid)
      .subscribe({
        next: (res: Array<LoadingBay>) => {
          this.loadingBays = res.map((el) => {
            return new LoadingBay(el);
          });

          this.loadingBaysList = res.map((el) => {
            return {
              name: `${el.name}`,
              value: el.uuid,
            }
          });
          this.writeValue(this.value);
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading loading bays for warehouse:', error);
          this.clearLoadingBays();
          this.loading = false;
        }
      });
  }

  private clearSelection(): void {
    this.value = undefined;
    this.localValue = undefined;
    this.onChange(undefined);
  }

  private clearLoadingBays(): void {
    this.loadingBays = [];
    this.loadingBaysList = [];
  }

  getFilteredList() {
    // No need to filter anymore since we're only loading loading bays for the selected warehouse
    return this.loadingBaysList;
  }

  getTitle(): string {
    if (!this.filterWarehouseUuid) {
      return "Please select a warehouse first";
    }
    if (this.loading) {
      return "Loading loading bays...";
    }
    if (this.loadingBaysList.length === 0) {
      return "No loading bays available for this warehouse";
    }
    return this.title;
  }


  onChange = (val: string | undefined) => {
  };

  onTouched = () => { };

  touched = false;
  disabled = false;

  writeValue(val?: string) {
    this.value = val;
    if (this.value != undefined || this.value != null) {
      this.localValue = this.loadingBaysList.filter((el) => el.value == val)[0];
    }
  }

  onValueChange(val?: CustomSelectOption) {
    this.value = val?.value;
    this.onChange(this.value);
    this.writeValue(this.value);
  }


  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return null;
  }
}
